<script setup lang="ts">
import { useStorage } from "@vueuse/core/index";
import { ref, watch, onBeforeUnmount, computed } from "vue";
import { debounce } from "vue-debounce";
import ActiveFilterDTO = App.Data.Explorer.Filter.ActiveFilterDTO;
import HierarchyQuickFilter from "@/modules/explorer/components/QuickFilter/HierarchyQuickFilter.vue";
import DefaultQuickFilter from "@/modules/explorer/components/QuickFilter/DefaultQuickFilter.vue";
import { useExplorerStore } from "@/modules/explorer/store";

const explorerStore = useExplorerStore();

const { quickFilters, isExplorer = false } = defineProps<{
	quickFilters: ActiveFilterDTO[];
	isExplorer?: boolean;
}>();

const quickFilterHeight = useStorage("quickFilterHeight", 250);
const heights = ref<Map<string, number>>(new Map());
const resizableDiv = ref<HTMLElement | null>(null);
let observer: ResizeObserver | null = null;

const computedHeight = computed({
	get: () => {
		const MAX_HEIGHT = 4.5;
		const values = quickFilters.map(
			(filter) => heights.value.get(filterToKey(filter)) ?? MAX_HEIGHT
		);

		return 40 + 36 * Math.min(MAX_HEIGHT, Math.max(...values, 1));
	},
	set: (height) => (quickFilterHeight.value = height),
});

watch(resizableDiv, (el, oldEl) => {
	if (oldEl && observer) {
		observer.unobserve(oldEl);
	}

	if (el) {
		if (!observer) {
			observer = new ResizeObserver((entries) => {
				for (const entry of entries) {
					setQuickFilterHeight(entry.contentRect.height);
				}
			});
		}

		observer.observe(el);
	}
});

onBeforeUnmount(() => {
	if (observer && resizableDiv.value) {
		observer.unobserve(resizableDiv.value);
	}
});

const setQuickFilterHeight = debounce((height: number) => {
	quickFilterHeight.value = height;
}, "200ms");

const isHierarchicalFilter = (filter: ActiveFilterDTO) =>
	(filter.dimension.hierarchy_definition_id ?? null) !== null;

const filterToKey = (filter: ActiveFilterDTO) =>
	`${filter.dimension.id}_${filter.dimension.hierarchy_definition_id}_${filter.dimension.dimension_bucket_id}`;

const setHeights = (key: string, height: number) => {
	heights.value.set(key, height);
};
</script>

<template>
	<v-card v-if="quickFilters.length" data-testid="quick-filter-menu">
		<div
			ref="resizableDiv"
			class="d-flex resizable"
			:style="`height: ${computedHeight}px`"
		>
			<template v-for="filter in quickFilters" :key="filterToKey(filter)">
				<hierarchy-quick-filter
					v-if="isHierarchicalFilter(filter)"
					:active-filter="filter"
					:is-explorer="isExplorer"
					:document-uuid="isExplorer ? explorerStore.document.uuid : ''"
					@resize="(height) => setHeights(filterToKey(filter), height)"
				/>
				<default-quick-filter
					v-else
					:active-filter="filter"
					:is-explorer="isExplorer"
					@resize="(height) => setHeights(filterToKey(filter), height)"
				/>
			</template>
		</div>
	</v-card>
</template>

<style scoped lang="scss">
.resizable {
	max-height: 40vh;
	min-height: 76px;
	resize: vertical;
	overflow: auto; // required for resize to work
}

::-webkit-scrollbar-track {
	background-color: rgb(var(--color-surface));
}

* {
	scrollbar-width: auto;
	scrollbar-color: #a1a1a2 rgb(var(--color-surface));
}
</style>
